import { CustomDateRange, ProjectGlobalData, ProjectSection, Report, ServiceItem, ServiceTransaction } from '../../types';
import { isAccountingEntry } from '../../services/serviceLogUtils';
import { getServiceLog } from '../../services/serviceJournal';
import { offenerDialogImDom, type DialogDokument, type DialogElement } from '../../services/appUmfang';
import { formatDateLocal, parseDateLocal } from '../../services/dateService';

// Anzeige-/Auflösungslogik des ProjectHub als pure Funktionen, damit sie ohne
// DOM-Testumgebung charakterisierbar ist (siehe projectHubLogic.test.ts).

interface NextInspectionNumberArgs {
    activeSectionId: string | null;
    sections?: ProjectSection[];
    dbCurrentNr: number | null;
    metaInspectionNumber?: number;
}

export const resolveNextInspectionNumber = ({ activeSectionId, sections, dbCurrentNr, metaInspectionNumber }: NextInspectionNumberArgs): number => {
    if (activeSectionId && activeSectionId !== 'all') {
        const section = sections?.find(s => s.id === activeSectionId);
        return (section?.currentInspectionNumber || 0) + 1;
    }
    return (dbCurrentNr || metaInspectionNumber || 0) + 1;
};

export const resolveEffectiveServices = (linkedParent: Report | null, globalData: ProjectGlobalData): ServiceItem[] =>
    linkedParent ? (linkedParent.metadata.globalData?.services || []) : (globalData.services || []);

export type ModuleNavTarget =
    | { kind: 'location-picker' }
    | { kind: 'sent-reports' }
    | { kind: 'navigate'; module: string };

export const resolveModuleNavTarget = (module: string): ModuleNavTarget => {
    if (module === 'project-map') return { kind: 'location-picker' };
    if (module === 'module-sent-reports') return { kind: 'sent-reports' };
    return { kind: 'navigate', module };
};

// A child project books onto its parent's LV — its log lives there too.
export const resolveServiceLogSource = (
    globalData: ProjectGlobalData,
    linkedParent: Report | null,
): ServiceTransaction[] =>
    ((globalData.linkedProjectConfig?.role === 'child' && linkedParent)
        ? getServiceLog(linkedParent)
        : getServiceLog({ metadata: { globalData } })) || [];

export const resolveServiceLogsForService = (
    globalData: ProjectGlobalData,
    linkedParent: Report | null,
    serviceId: string,
): ServiceTransaction[] =>
    // Accounting bookings (BU-…) are invoice postings, not performed work —
    // keep them out of the per-service history shown in the tracking modal.
    resolveServiceLogSource(globalData, linkedParent).filter(l => l.serviceId === serviceId && !isAccountingEntry(l));

export interface CustomDateRangeDraft {
    label: string;
    startDate: string;
    endDate: string;
}

export const emptyCustomDateRangeDraft = (): CustomDateRangeDraft => ({ label: '', startDate: '', endDate: '' });

// ISO-Datumsstrings (YYYY-MM-DD) sind lexikografisch vergleichbar — kein Date-Parsing nötig.
export const isValidCustomDateRangeDraft = (draft: CustomDateRangeDraft): boolean =>
    draft.label.trim().length > 0
    && draft.startDate.length > 0
    && draft.endDate.length > 0
    && draft.startDate <= draft.endDate;

export const addCustomDateRange = (
    existing: CustomDateRange[] | undefined,
    draft: CustomDateRangeDraft,
    id: string,
): CustomDateRange[] => {
    const current = existing || [];
    if (!isValidCustomDateRangeDraft(draft)) return current;
    const range: CustomDateRange = {
        id,
        label: draft.label.trim(),
        startDate: draft.startDate,
        endDate: draft.endDate,
    };
    return [...current, range].sort((a, b) => a.startDate.localeCompare(b.startDate));
};

export const removeCustomDateRange = (existing: CustomDateRange[] | undefined, id: string): CustomDateRange[] =>
    (existing || []).filter(r => r.id !== id);

// "[Wiederholung]" ist eine rein interne Markierung des Sendeverlaufs und darf
// nie im Betreff der Kunden-Mail landen. Alt-Logs können das Präfix bereits
// (auch mehrfach gestapelt) tragen — deshalb alle führenden Vorkommen strippen.
export const stripRepeatPrefix = (subject: string): string =>
    subject.replace(/^(\[Wiederholung\]\s*)+/, '');

// Log-Betreff für den Wiederholungsversand: genau ein Präfix, daran erkennt
// der Sendeverlauf die Wiederholung (includes-Check). Idempotent, damit beim
// erneuten Versand eines Wiederholungs-Logs nichts weiter gestapelt wird.
export const markRepeatSubject = (subject: string): string =>
    `[Wiederholung] ${stripRepeatPrefix(subject)}`;

// ─── Begehungsknopf (Handy) ─────────────────────────────────────────────────

/**
 * Was der Begehungsknopf oben im Hub anbietet:
 * - 'fortsetzen': offener Entwurf — der Knopf fuehrt direkt ins Modul.
 * - 'starten': kein offener Entwurf (auch vor der ersten Begehung) — Dialog.
 * - 'begruenden': fremdes Projekt ohne Begruendung von heute — erst der
 *   Begruendungsdialog, danach der Begehungsdialog (handleOpenEditorFromGrid).
 * - 'ansehen': sonstige Nur-Ansicht (Offene Positionen, fremde Sperre).
 * - 'deaktiviert': reportExempt.
 */
export type BegehungsKnopfArt = 'fortsetzen' | 'starten' | 'begruenden' | 'ansehen' | 'deaktiviert';

export interface BegehungsKnopfZustand {
    art: BegehungsKnopfArt;
    /** Offener Entwurf: dessen Nummer; sonst die des naechsten Berichts. */
    nummer: number;
    entwurfOffen: boolean;
    /**
     * Begehungsdatum des offenen Entwurfs — nur, wenn der geladene Bericht
     * dieser Entwurf ist. „fortsetzen" fragt es nicht mehr ab, der Versand
     * bucht darauf; der Knopf nennt es deshalb.
     */
    entwurfDatum?: string;
}

interface BegehungsKnopfArgs {
    reportExempt: boolean;
    isReadOnly: boolean;
    /** Fremdes Projekt, heute noch nicht begruendet (ForeignAccessContext.pending). */
    begruendungMoeglich: boolean;
    inspectionNumber?: number;
    isExported?: boolean;
    dbCurrentNr?: number | null;
    dbLastExportedNr?: number | null;
    /** Begehungsdatum des geladenen Berichts (metadata.date). */
    datum?: string;
}

/**
 * Zaehler und Entwurf wie im Begehungsdialog (LoadReportDialog: currentNrVal,
 * lastExportedVal, hasOpenDraft) — der Knopf verspricht, was der Dialog zeigt.
 * Die naechste Nummer ist die, die handleReportAction('next') vergibt.
 * Fremdprojekt vor Lesemodus: handleOpenEditorFromGrid prueft es ebenso zuerst.
 */
export const resolveBegehungsKnopf = (a: BegehungsKnopfArgs): BegehungsKnopfZustand => {
    const nr = a.inspectionNumber || 0;
    const aktuell = Math.max(a.dbCurrentNr || 0, nr);
    const exportiert = a.dbLastExportedNr ?? (a.isExported ? nr : Math.max(0, nr - 1));
    const entwurfOffen = aktuell > exportiert;
    const art: BegehungsKnopfArt = a.reportExempt ? 'deaktiviert'
        : a.begruendungMoeglich ? 'begruenden'
        : a.isReadOnly ? 'ansehen'
        : entwurfOffen ? 'fortsetzen' : 'starten';
    // Liegt die DB vor dem geladenen Bericht, gehoert dessen Datum nicht zum Entwurf.
    const entwurfDatum = entwurfOffen && nr === aktuell && a.datum ? a.datum : undefined;
    return { art, nummer: entwurfOffen ? aktuell : aktuell + 1, entwurfOffen, ...(entwurfDatum ? { entwurfDatum } : {}) };
};

export interface BegehungsKnopfTexte {
    titel: string;
    /** Hinweiszeile (Nur-Ansicht, deaktiviert, Datum des Entwurfs). */
    hinweis?: string;
    /** Entwurf nicht von heute: die Hinweiszeile hebt sich ab. */
    hinweisBetont?: boolean;
    /** Nur mit wartenden Freimeldungen — der Zaehler liegt im Hub ohnehin vor. */
    freimeldungen?: string;
}

/**
 * „Entwurf von heute" bzw. „Entwurf vom 25.09." (aus einem anderen Jahr mit
 * Jahr) — betont, wenn nicht von heute. Unlesbares Datum: keine Zeile.
 */
const entwurfDatumZeile = (datum: string | undefined, heute: string | undefined): { text: string; betont: boolean } | undefined => {
    const d = parseDateLocal(datum);
    if (!d) return undefined;
    if (formatDateLocal(d) === heute) return { text: 'Entwurf von heute', betont: false };
    const tag = `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
    const jahr = heute?.slice(0, 4) === String(d.getFullYear()) ? '' : String(d.getFullYear());
    return { text: `Entwurf vom ${tag}${jahr}`, betont: heute !== undefined };
};

// Nur-Ansicht und deaktiviert im Wortlaut der Kachel (ProjectModuleGrid/SifaModuleGrid).
// `heute`: lokal als YYYY-MM-DD, fuer die Datumszeile des Entwurfs.
export const begehungsKnopfTexte = (z: BegehungsKnopfZustand, offeneFreimeldungen = 0, heute?: string): BegehungsKnopfTexte => {
    const freimeldungen = offeneFreimeldungen > 0
        ? (offeneFreimeldungen === 1 ? '1 Freimeldung wartet' : `${offeneFreimeldungen} Freimeldungen warten`)
        : undefined;
    switch (z.art) {
        case 'deaktiviert':
            return { titel: 'Begehungsbericht', hinweis: 'Für dieses Projekt deaktiviert' };
        case 'fortsetzen': {
            const zeile = entwurfDatumZeile(z.entwurfDatum, heute);
            return { titel: `Begehung #${z.nummer} fortsetzen`, hinweis: zeile?.text, hinweisBetont: zeile?.betont, freimeldungen };
        }
        case 'starten':
            return { titel: `Begehung #${z.nummer} starten`, freimeldungen };
        case 'begruenden':
            return { titel: `Begründen und Begehung #${z.nummer} ${z.entwurfOffen ? 'fortsetzen' : 'starten'}`, freimeldungen };
        case 'ansehen':
            return { titel: 'Begehungsbericht ansehen', hinweis: 'Nur-Ansicht: keine neue Begehung', freimeldungen };
    }
};

/**
 * Liegt IN der Flaeche ein eigener offener Dialog (Sendeverlauf: Vorschau,
 * Wiederholungsversand, Archivauswahl)? Er meldet sich nicht selbst fuer die
 * Zurueck-Taste an; der Rahmen laesst sie dann stehen wie bisher, statt samt
 * halb getroffener Auswahl zu schliessen. Sichtbarkeit wie offenerDialogImDom.
 */
export const innererDialogOffen = (
    flaeche: { querySelectorAll(selektor: string): ArrayLike<DialogElement> },
    ansicht: DialogDokument['defaultView'],
): boolean => offenerDialogImDom({ querySelectorAll: (s) => flaeche.querySelectorAll(s), defaultView: ansicht });

/**
 * Zurueck-Taste im Hub-Overlay (nur wo es sie anmeldet): wie der
 * Schliessen-Knopf — ausser, darin ist ein eigener Dialog offen. true = geschlossen.
 */
export const hubOverlayZurueck = (
    flaeche: {
        querySelectorAll(selektor: string): ArrayLike<DialogElement>;
        ownerDocument: { defaultView: DialogDokument['defaultView'] };
    } | null,
    schliessen: () => void,
): boolean => {
    if (flaeche && innererDialogOffen(flaeche, flaeche.ownerDocument.defaultView)) return false;
    schliessen();
    return true;
};
